/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {createSSOConfiguration} from '@ion/core/src/services/Connect';
import {SSOConfiguration} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createMutationResult} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseAddSSOConfigurationResult = UseDefaultQueryResult & {
  save: (ssoConfig: SSOConfiguration) => void;
};

const createSSO = async(ssoConfig: SSOConfiguration) => {
  try {
    const created = await createSSOConfiguration(ssoConfig);
    return created;
  } catch (err) {
    error(err);
    throw new Error(err);
  }
};

/**
 * Common hook for adding a SSO Configuration entity.
 *
 * ```typescript
 *
 *    import useAddSSOConfiguration from '@ion/query/src/hooks/';
 *
 *    const {save, isSuccess, isError} = useAddSSOConfiguration();
 *
 * ```
 */
export const useAddSSOConfiguration = (): UseAddSSOConfigurationResult => {
  const queryClient = useQueryClient();

  const mutation    = useMutation(createSSO, {
    onSettled : () => {
      queryClient.invalidateQueries([QueryKey.ADD_SSO_CONFIGURATION]);
    }
  });

  const save = useCallback((ssoConfig: SSOConfiguration) => {
    mutation.mutate(ssoConfig);
  }, [mutation]);

  return createMutationResult(
    mutation, {save}
  ) as UseAddSSOConfigurationResult;
};

export default useAddSSOConfiguration;
